'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Bike, ChevronRight } from 'lucide-react';

const slides = [
  {
    image:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80&auto=format&fit=crop',
    tag: 'Reparación General',
    tagSub: 'Motor · Frenos · Suspensión · Eléctrico',
  },
  {
    image:
      'https://images.unsplash.com/photo-1449426468159-d96dbf08f19f?w=1920&q=80&auto=format&fit=crop',
    tag: 'Mantenimiento Preventivo',
    tagSub: 'Aceite · Filtros · Bujías · Revisión Total',
  },
  {
    image:
      'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=1920&q=80&auto=format&fit=crop',
    tag: 'Personalización & Tunning',
    tagSub: 'Escapes · LED · Accesorios Performance',
  },
  {
    image:
      'https://images.unsplash.com/photo-1609630875171-b1321377ee65?w=1920&q=80&auto=format&fit=crop',
    tag: 'Repuestos Originales',
    tagSub: 'Stock Completo · Entrega Inmediata',
  },
];

const stats = [
  { value: '5,000+', label: 'Motos Reparadas' },
  { value: '12+', label: 'Años de Experiencia' },
  { value: '98%', label: 'Clientes Satisfechos' },
  { value: '24h', label: 'Respuesta Rápida' },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [badgeKey, setBadgeKey] = useState(0);

  const goToSlide = useCallback((index: number) => {
    setCurrent(index);
    setBadgeKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => {
        const next = (prev + 1) % slides.length;
        setBadgeKey((k) => k + 1);
        return next;
      });
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background images — fade crossfade */}
      {slides.map((slide, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="absolute inset-0 bg-center bg-cover transition-opacity duration-1000"
          style={{
            backgroundImage: `url(${slide.image})`,
            opacity: i === current ? 1 : 0,
            zIndex: 0,
          }}
        />
      ))}

      {/* Dark overlay — keeps branding dark */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to bottom, rgba(10,10,10,0.72) 0%, rgba(10,10,10,0.60) 50%, rgba(10,10,10,0.88) 100%)', zIndex: 1 }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative w-full max-w-7xl mx-auto px-4 pt-32 pb-16 text-center" style={{ zIndex: 2 }}>

        {/* Dynamic service badge */}
        <div
          key={badgeKey}
          className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 text-sm text-slate-200 mb-8 animate-fade-slide-up"
        >
          <Bike className="w-4 h-4 text-[#FF4500] shrink-0" />
          <span className="font-medium">{slides[current].tag}</span>
          <span className="text-white/30 mx-0.5">·</span>
          <span className="text-white/55 text-xs">{slides[current].tagSub}</span>
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold leading-tight mb-6 drop-shadow-lg">
          ¿Tu moto parada?{' '}
          <span className="bg-gradient-to-r from-[#E31B23] via-[#FF4500] to-[#FF6B35] bg-clip-text text-transparent">
            Nosotros la
            <br className="hidden sm:block" /> ponemos a rugir
          </span>
        </h1>

        <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
          Cada minuto sin tu moto es tiempo perdido. En LIVE MOTO — Baños de Agua Santa —
          diagnosticamos rápido, trabajamos con garantía y te mantenemos informado en cada paso.{' '}
          <strong className="text-white">Primera revisión sin costo.</strong>
        </p>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          <a
            href="#contacto"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-[#E31B23] to-[#FF4500] hover:from-[#FF2D35] hover:to-[#FF6B35] text-white font-semibold text-lg transition-all shadow-xl shadow-[#E31B23]/30"
          >
            Agenda Ahora — Es Gratis
            <ChevronRight className="w-5 h-5" />
          </a>
          <a
            href="https://wa.me/593995254062"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl border border-white/20 bg-white/5 hover:bg-white/12 backdrop-blur-sm text-white font-semibold text-lg transition-all"
          >
            WhatsApp: 099 525 4062
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mb-14">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-[#E31B23] to-[#FF4500] bg-clip-text text-transparent drop-shadow">
                {stat.value}
              </div>
              <div className="text-sm text-slate-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Slide indicator tabs */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          {slides.map((slide, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-medium transition-all duration-300 cursor-pointer ${
                i === current
                  ? 'bg-[#E31B23]/80 border-[#E31B23] text-white shadow-lg shadow-[#E31B23]/20'
                  : 'bg-white/8 border-white/20 text-white/50 hover:bg-white/15 hover:text-white/80'
              }`}
              aria-label={`Ver servicio: ${slide.tag}`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full shrink-0 ${i === current ? 'bg-white' : 'bg-white/40'}`}
              />
              <span className="hidden sm:inline">{slide.tag}</span>
            </button>
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-6 flex items-center justify-center gap-1.5">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`h-0.5 rounded-full transition-all duration-500 ${
                i === current ? 'w-8 bg-[#E31B23]' : 'w-4 bg-white/20'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
